import { ApplicationRef, ComponentFactoryResolver, Injectable, Injector } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs/index';
import { UpgradedProviders } from './upgraded-providers';
import { map } from 'rxjs/operators';

/**
 * Since we're first bootstrapping the Angular AppModule and then the angularJS module (autoremind) the angularJS
 * injector is not available yet when Angular constructors inject dependencies. So we can't inject upgraded services
 * until later.
 * This service is a holder of all upgraded service providers and the main AppModule is calling this service when
 * ready to inject all upgraded services and then notify any subscribes who have injected this service. Then
 * These subscribers can request the services they need.
 */
@Injectable({
    providedIn: 'root' // No need to provide in AppModule, and now tree shakable
})
export class UpgradedProviderService {

    private services = {};
    /** Observable to when when upgraded services are available - we don't use the value for anything */
    private servicesInjected$ = new ReplaySubject(1);
    initialized = false;

    constructor(private resolver: ComponentFactoryResolver,
                private applicationRef: ApplicationRef,
                private injector: Injector) {
    }

    injectServices(): void {
        UpgradedProviders.providers
            .forEach(provider => {
                // Get the angularJS service from the angularJS injector
                this.services[provider.provide] = this.injector.get(provider.provide);
            });

        this.servicesInjected$.next();
        this.initialized = true;
    }

    /**
     * If for example a service is requesting an upgraded service it must be done subscribing to the observable returned
     * by this method. By using a ReplaySubject we'll ensure that the subscriber will either be notified as soon as
     * upgraded services are available or if they are already available the subscriber will get it right away.
     * @param name the name of the upgraded service
     */
    getServiceAsync(name: string): Observable<void> {
        return this.servicesInjected$.asObservable()
            .pipe(
                map(_ => this.services[name]) // Provide the service to the subscribers
            );
    }
}
